/**
 * `useLiveDocsBlock` — the canonical fixture-vs-live adapter for live-aware
 * docs blocks (PRD-9a, ADR-006, ADR-007).
 *
 * Single mode-switch helper consumed by every live-aware block:
 *   - **Anonymous** (no `useAuth` provider, or `authenticated === false`)
 *     → returns `{ data: fixture, source: 'fixture' }`. **No network call.**
 *   - **Signed-in** → calls `useQuery({ queryKey, queryFn })` and returns
 *     `{ data, source: 'live' }` on success. On failure (or while loading
 *     with no cached data), returns `{ data: fixture, isFallback: true,
 *     source: 'live-fallback' }`.
 *   - **Storybook / SSR** (no auth provider mounted) → behaves as anonymous.
 *   - **Force-fixture override** (via {@link LiveDocsBlockModeProvider}) →
 *     returns the fixture even for a signed-in reader. This is the
 *     integration seam for [Guided Demos PRD-G2](../../../../../../docs/internal/product/guided-demos.md):
 *     guided demos' simulation shell wraps the docs renderer and forces
 *     fixtures so simulation mode never talks to live workspace data.
 *
 * The selector is the **only** mode-switch in the system. Every live-aware
 * block reads its data through this hook exactly once at the top so the
 * underlying component tree is identical across modes — Storybook stories,
 * anonymous web rendering, and signed-in rendering produce the same DOM
 * (only the fixture rows differ from the live rows).
 */
import { useContext } from 'react';
import { useQuery, type Query } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { AuthContext } from '@reflex/ui/client/context';
import { LiveDocsBlockModeContext } from './LiveDocsBlockMode.js';

export type LiveDocsBlockSource = 'fixture' | 'live' | 'live-fallback';

export interface LiveDocsBlockResult<T> {
  /** The data the block should render. Always defined — fixture is the floor. */
  data: T;
  /**
   * `true` when the live data path **failed** and the fixture is being
   * rendered as a fallback. The block is responsible for surfacing this
   * through {@link LiveBlockBoundary}. Note: `isFallback` is `false` during
   * initial loading — we show the fixture but don't mark it as a failure
   * until the request actually errors.
   */
  isFallback: boolean;
  /**
   * `true` when the live data is being fetched but hasn't resolved yet.
   * The block can use this to show a subtle loading indicator without
   * displaying a failure notice.
   */
  isLoading: boolean;
  /** Where the data ultimately came from — useful in tests and stories. */
  source: LiveDocsBlockSource;
  /**
   * The underlying live error (if any). Always `null` on the fixture / no-auth
   * paths so callers wiring this hook into existing `useQuery`-shaped surfaces
   * (e.g. `useAgentModelSupport`) can pass it through unchanged.
   */
  error: Error | null;
}

export interface UseLiveDocsBlockOptions<T> {
  /**
   * React Query key the live data is read from. Use the **same** key the
   * rest of the web app uses for that data (e.g. `orgPluginsQueryKey(orgId)`)
   * so a signed-in reader's already-loaded list reads from cache for free.
   */
  queryKey: QueryKey;
  /** Async fetcher invoked only when signed-in and the cache is empty. */
  queryFn: () => Promise<T>;
  /** Fixture rendered for anonymous readers, in Storybook, and on live failure. */
  fixture: T;
  /**
   * Optional `enabled` override. Defaults to `true` for the signed-in path —
   * pass `false` to gate live fetching behind component-specific
   * preconditions (e.g. an `orgId` not yet known).
   */
  enabled?: boolean;
  /**
   * Optional pass-through to the underlying `useQuery`'s `refetchInterval`.
   * Used by the launch-form hooks to keep their existing polling behavior
   * (e.g. blueprints poll while builds are in flight) when wrapped in this
   * adapter. Ignored on the anonymous / fixture path.
   */
  refetchInterval?: number | false | ((query: Query<T>) => number | false);
  /** Optional pass-through to the underlying `useQuery`'s `staleTime`. */
  staleTime?: number;
}

/**
 * Fixture-vs-live selector for live-aware docs blocks. Returns the fixture
 * on the anonymous path (or when the simulation-mode override is active),
 * the live React Query result when signed in and the request succeeds, and
 * the fixture with `isFallback: true` on live failure.
 *
 * The selector reads `AuthContext` directly via `useContext(AuthContext)`
 * so the public docs route shell — which mounts inside `AuthProvider` —
 * works correctly, and so the hook degrades gracefully to "anonymous" when
 * no provider is mounted (Storybook decorators, jsdom unit tests).
 */
export function useLiveDocsBlock<T>(options: UseLiveDocsBlockOptions<T>): LiveDocsBlockResult<T> {
  const { queryKey, queryFn, fixture, enabled = true, refetchInterval, staleTime } = options;
  const auth = useContext(AuthContext);
  const mode = useContext(LiveDocsBlockModeContext);

  const isAuthenticated = auth?.authenticated === true;
  const forceFixture = mode === 'fixture';

  // Anonymous (no auth context, not signed in, or simulation-mode override
  // active) — short-circuit with the fixture and never touch the network.
  // `useQuery` is still mounted with `enabled: false` so the rules-of-hooks
  // contract is preserved across re-renders if `authenticated` flips.
  const liveEnabled = isAuthenticated && !forceFixture && enabled;

  const query = useQuery({
    queryKey,
    queryFn,
    enabled: liveEnabled,
    retry: false,
    // Live-aware blocks are passive: a stale read from cache is fine, and
    // we don't want to refetch on every focus while the docs page sits
    // open in a background tab.
    refetchOnWindowFocus: false,
    ...(refetchInterval !== undefined ? { refetchInterval } : {}),
    ...(staleTime !== undefined ? { staleTime } : {}),
  });

  if (!liveEnabled) {
    return {
      data: fixture,
      isFallback: false,
      isLoading: false,
      source: 'fixture',
      error: null,
    };
  }

  // Error state: the request failed — show fixture with fallback indicator
  if (query.isError) {
    return {
      data: fixture,
      isFallback: true,
      isLoading: false,
      source: 'live-fallback',
      error: query.error as Error | null,
    };
  }

  // Loading state: request in progress, show fixture without fallback indicator
  // (we don't want to show a failure notice while the data is still loading)
  if (query.isLoading && query.data === undefined) {
    return {
      data: fixture,
      isFallback: false,
      isLoading: true,
      source: 'fixture',
      error: null,
    };
  }

  // Success: live data available
  if (query.data !== undefined) {
    return {
      data: query.data,
      isFallback: false,
      isLoading: false,
      source: 'live',
      error: null,
    };
  }

  // Fallback: shouldn't reach here, but treat as error
  return {
    data: fixture,
    isFallback: true,
    isLoading: false,
    source: 'live-fallback',
    error: query.error as Error | null,
  };
}
